import { SearchAlertChannelFields } from '@/components/search-alert-channel-fields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { apiClient } from '@/lib/apiClient';
import { applyLabelRemovals, buildDefaultAlertName, type CreateAlertDraft, extractValidationErrors } from '@/lib/search-alerts';
import * as advertiserAlertRoutes from '@/routes/advertiser/alerts';
import * as dashboardAlertRoutes from '@/routes/dashboard/alerts';
import { type SharedData } from '@/types';
import {
    type SearchAlertChannelValues,
    type SearchAlertCriteria,
    type SearchAlertFormErrors,
    type SearchAlertItem,
    type SearchAlertSource,
} from '@/types/search-alert';
import { Link, usePage } from '@inertiajs/react';
import { BellRing, CheckCircle2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const FALLBACK_VALIDITY_OPTIONS = [30, 90, 180, 360];
const FALLBACK_DEFAULT_VALIDITY = 90;
const GENERIC_ERROR = 'Não foi possível criar o alerta agora. Tente novamente em instantes.';
// Errors shown next to their own field; every other one goes to the general message.
const INLINE_ERROR_FIELDS = new Set(['name', 'channel_email', 'phone', 'whatsapp_consent', 'validity_days']);

interface MatchesCountResponse {
    count: number;
    search_url: string;
}

interface StoreAlertResponse {
    message: string;
    alert: SearchAlertItem;
}

type MatchesState = { status: 'idle' | 'loading' } | { status: 'ready'; count: number; searchUrl: string } | { status: 'error' };

interface CreateAlertDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    criteria: SearchAlertCriteria;
    /** Where the modal was opened from (stored in search_alerts.source). */
    source: SearchAlertSource;
    /** Choices restored from sessionStorage after the login round-trip. */
    initialDraft?: CreateAlertDraft | null;
    onCreated?: (alert: SearchAlertItem) => void;
}

const channelsLabel = (email: boolean, whatsapp: boolean) => (email && whatsapp ? 'e-mail e WhatsApp' : whatsapp ? 'WhatsApp' : 'e-mail');

/**
 * "Criar alerta" modal (CN-192, layout of CN-200 / atualização 17-10): the criteria as removable chips, how many boats
 * exist today, the suggested name (the smart-search phrase when there is one), channels and validity. Requires an
 * authenticated user (the caller sends guests to the login first).
 */
export function CreateAlertDialog({ open, onOpenChange, criteria, source, initialDraft = null, onCreated }: CreateAlertDialogProps) {
    const { auth, searchAlerts } = usePage<SharedData>().props;
    const user = auth?.user;
    const validityOptions = searchAlerts?.validity_options?.length ? searchAlerts.validity_options : FALLBACK_VALIDITY_OPTIONS;
    const defaultValidity = searchAlerts?.default_validity_days ?? FALLBACK_DEFAULT_VALIDITY;
    const consentText = searchAlerts?.whatsapp_consent_text ?? '';
    const hasPhone = Boolean(user?.has_phone);
    // Advertisers are redirected away from /dashboard (role.redirect), so their alerts live under /anunciante/alerts.
    const { index: alertsIndex, matchesCount, store: storeAlert } = user?.role === 'sponsor' ? advertiserAlertRoutes : dashboardAlertRoutes;

    const [removedLabelIds, setRemovedLabelIds] = useState<Set<string>>(() => new Set());
    const [name, setName] = useState('');
    const [nameEdited, setNameEdited] = useState(false);
    const [validityDays, setValidityDays] = useState<number>(defaultValidity);
    const [channels, setChannels] = useState<SearchAlertChannelValues>({
        channel_email: true,
        channel_whatsapp: false,
        whatsapp_consent: false,
        phone: '',
    });
    const [errors, setErrors] = useState<SearchAlertFormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdAlert, setCreatedAlert] = useState<SearchAlertItem | null>(null);
    const [createdValidity, setCreatedValidity] = useState<number>(defaultValidity);
    const [matches, setMatches] = useState<MatchesState>({ status: 'idle' });
    const matchesRequestRef = useRef(0);

    const active = useMemo(() => applyLabelRemovals(criteria, removedLabelIds), [criteria, removedLabelIds]);
    const hasCriteria = active.labels.length > 0;
    // The phrase names the alert while the criteria are the ones the AI understood; after a removal, the chips do.
    const suggestedName = useMemo(
        () => (criteria.originalText && removedLabelIds.size === 0 ? criteria.defaultName : buildDefaultAlertName(active.labels.map((label) => label.text))),
        [active.labels, criteria.defaultName, criteria.originalText, removedLabelIds.size],
    );

    // Every opening starts from the current criteria and the draft/profile defaults.
    useEffect(() => {
        if (!open) {
            return;
        }

        setRemovedLabelIds(new Set());
        setName(initialDraft?.name ?? criteria.defaultName);
        setNameEdited(Boolean(initialDraft?.name));
        setValidityDays(
            initialDraft?.validity_days && validityOptions.includes(initialDraft.validity_days) ? initialDraft.validity_days : defaultValidity,
        );
        // E-mail follows the profile preference; WhatsApp always starts off (the user turns it on and gives the consent).
        setChannels({
            channel_email: initialDraft?.channel_email ?? user?.notifications_email ?? true,
            channel_whatsapp: initialDraft?.channel_whatsapp ?? false,
            whatsapp_consent: false,
            phone: '',
        });
        setErrors({});
        setCreatedAlert(null);
        setIsSubmitting(false);
        // Only when the modal opens: the defaults must not be re-applied while the user is typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // The suggested name follows the chips until the user edits it.
    useEffect(() => {
        if (open && !nameEdited) {
            setName(suggestedName);
        }
    }, [nameEdited, open, suggestedName]);

    // "Hoje já existem N embarcações assim."
    useEffect(() => {
        if (!open || !hasCriteria) {
            setMatches({ status: 'idle' });
            return;
        }

        const requestId = ++matchesRequestRef.current;
        setMatches({ status: 'loading' });

        apiClient
            .get<MatchesCountResponse>(matchesCount.url(), { params: { filters: active.params } })
            .then((response) => {
                if (matchesRequestRef.current === requestId) {
                    setMatches({ status: 'ready', count: response.data.count, searchUrl: response.data.search_url });
                }
            })
            .catch((error: unknown) => {
                if (matchesRequestRef.current !== requestId) {
                    return;
                }

                console.error('Error counting the boats of the search alert:', error);
                setMatches({ status: 'error' });
            });
    }, [open, hasCriteria, active.params, matchesCount]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!hasCriteria || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        const payload: Record<string, unknown> = {
            name: name.trim(),
            filters: active.params,
            validity_days: validityDays,
            channel_email: channels.channel_email,
            channel_whatsapp: channels.channel_whatsapp,
            source,
        };

        if (criteria.originalText) {
            payload.original_text = criteria.originalText;
        }

        if (channels.channel_whatsapp) {
            payload.whatsapp_consent = channels.whatsapp_consent;

            if (!hasPhone) {
                payload.phone = channels.phone;
            }
        }

        try {
            const response = await apiClient.post<StoreAlertResponse>(storeAlert.url(), payload);

            setCreatedValidity(validityDays);
            setCreatedAlert(response.data.alert);
            onCreated?.(response.data.alert);
        } catch (error: unknown) {
            const validationErrors = extractValidationErrors(error);

            if (validationErrors) {
                setErrors(validationErrors);
            } else {
                console.error('Error creating the search alert:', error);
                setErrors({ general: GENERIC_ERROR });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Any error without a field of its own (e.g. original_text) is shown here, so a refused save is never silent.
    const unplacedError = Object.entries(errors).find(([field]) => !INLINE_ERROR_FIELDS.has(field))?.[1];
    const generalError = errors.general ?? errors.limit ?? errors.filters ?? errors.source ?? unplacedError ?? null;
    const formId = 'create-alert-form';
    const submitBlockedReason = !hasCriteria
        ? 'Escolha pelo menos um filtro'
        : channels.channel_whatsapp && !channels.whatsapp_consent
          ? 'Aceite o termo do WhatsApp'
          : channels.channel_whatsapp && !hasPhone && !channels.phone.trim()
            ? 'Informe seu celular'
            : !channels.channel_email && !channels.channel_whatsapp
              ? 'Escolha um canal'
              : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
                {createdAlert ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CheckCircle2 aria-hidden="true" className="h-5 w-5 text-green-600" />
                                Alerta criado!
                            </DialogTitle>
                            <DialogDescription>
                                Todo dia às 9h, se aparecer uma embarcação nova assim, avisamos por{' '}
                                <strong>{channelsLabel(createdAlert.channel_email, createdAlert.channel_whatsapp)}</strong>. Válido por{' '}
                                <strong>{createdValidity} dias</strong>.
                            </DialogDescription>
                        </DialogHeader>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Continuar buscando
                            </Button>
                            <Button asChild className="bg-[#0027b2] hover:bg-[#041F4A]">
                                <Link href={alertsIndex().url}>Ver meus alertas</Link>
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <BellRing aria-hidden="true" className="h-5 w-5 text-[#0027b2]" />
                                Criar alerta
                            </DialogTitle>
                            <DialogDescription>Avisamos quando aparecer uma embarcação nova assim.</DialogDescription>
                        </DialogHeader>

                        <form id={formId} onSubmit={handleSubmit} className="space-y-5" noValidate>
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-900">O que vamos procurar para você</p>
                                {hasCriteria ? (
                                    <ul className="flex flex-wrap gap-2" aria-label="Critérios do alerta">
                                        {active.labels.map((label) => (
                                            <li key={label.id}>
                                                <span className="inline-flex items-center rounded-full border border-gray-300 pl-3 text-sm text-gray-800">
                                                    <span className="py-1">{label.text}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setRemovedLabelIds((previous) => new Set(previous).add(label.id))}
                                                        aria-label={`Tirar ${label.text}`}
                                                        className="ml-1 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                                    >
                                                        <X aria-hidden="true" className="h-3.5 w-3.5" />
                                                    </button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-600">
                                        Nenhum filtro selecionado. Escolha categoria, localização, ano, preço ou outro filtro na busca para criar um
                                        alerta.
                                    </p>
                                )}
                                {removedLabelIds.size > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setRemovedLabelIds(new Set())}
                                        className="text-xs font-medium text-[#0027b2] underline-offset-2 hover:underline"
                                    >
                                        Voltar aos critérios da busca
                                    </button>
                                )}
                                {criteria.excludedText && (
                                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note">
                                        O trecho “{criteria.excludedText}” não entra no alerta, que avisa só pelos filtros.
                                    </p>
                                )}
                            </div>

                            {hasCriteria && (
                                <p className="text-sm text-gray-700" aria-live="polite">
                                    {matches.status === 'loading' && (
                                        <span className="inline-flex items-center gap-2">
                                            <Spinner aria-label="Contando embarcações" /> Contando embarcações assim…
                                        </span>
                                    )}
                                    {matches.status === 'ready' && (
                                        <>
                                            Hoje já {matches.count === 1 ? 'existe' : 'existem'} <strong>{matches.count.toLocaleString('pt-BR')}</strong>{' '}
                                            {matches.count === 1 ? 'embarcação' : 'embarcações'} assim. O alerta avisa só das novas.{' '}
                                            {matches.count > 0 && (
                                                <a href={matches.searchUrl} className="font-medium text-[#0027b2] underline-offset-2 hover:underline">
                                                    Ver agora
                                                </a>
                                            )}
                                        </>
                                    )}
                                    {matches.status === 'error' && (
                                        <span className="text-gray-500">Não foi possível contar as embarcações agora.</span>
                                    )}
                                </p>
                            )}

                            <div className="space-y-1.5">
                                <Label htmlFor="create-alert-name">Nome do alerta</Label>
                                <Input
                                    id="create-alert-name"
                                    name="name"
                                    value={name}
                                    maxLength={120}
                                    onChange={(event) => {
                                        setNameEdited(true);
                                        setName(event.target.value);
                                    }}
                                    aria-invalid={Boolean(errors.name)}
                                    aria-describedby={errors.name ? 'create-alert-name-error' : undefined}
                                />
                                {errors.name && (
                                    <p id="create-alert-name-error" role="alert" className="text-sm text-red-600">
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            <SearchAlertChannelFields
                                values={channels}
                                onChange={setChannels}
                                errors={errors}
                                consentText={consentText}
                                hasPhone={hasPhone}
                                requireConsent
                                idPrefix="create-alert"
                            />

                            <div className="space-y-1.5">
                                <Label htmlFor="create-alert-validity">Por quanto tempo</Label>
                                <Select value={String(validityDays)} onValueChange={(value) => setValidityDays(Number(value))}>
                                    <SelectTrigger id="create-alert-validity" aria-invalid={Boolean(errors.validity_days)}>
                                        <SelectValue placeholder="Escolha a validade" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {validityOptions.map((days) => (
                                            <SelectItem key={days} value={String(days)}>
                                                {days} dias
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.validity_days && (
                                    <p role="alert" className="text-sm text-red-600">
                                        {errors.validity_days}
                                    </p>
                                )}
                            </div>

                            {generalError && (
                                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    {generalError}
                                </p>
                            )}
                        </form>

                        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    form={formId}
                                    disabled={Boolean(submitBlockedReason) || isSubmitting}
                                    title={submitBlockedReason ?? undefined}
                                    className="bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f]"
                                >
                                    {isSubmitting && <Spinner aria-label="Salvando" />}
                                    <BellRing aria-hidden="true" className="h-4 w-4" />
                                    {submitBlockedReason ?? 'Criar alerta'}
                                </Button>
                            </div>
                            <p className="text-center text-xs text-gray-500 sm:text-right">Você pode pausar ou excluir quando quiser em Meus alertas.</p>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
